import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCoins,
  faMoneyCheckDollar,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../dash.css';
import {
  formatDateTime,
  formatKes,
  getMonthKey,
  getMonthLabel,
  summarizePayroll
} from '../../utils/financeWorkflow';

const normalizeEmployeeStatus = (value) => String(value || 'Active').trim();
const employeeStatusClass = (value) => normalizeEmployeeStatus(value).toLowerCase();

function PaySalariesComp() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [statusFilter, setStatusFilter] = useState('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const [employeeSnapshot, paymentSnapshot] = await Promise.all([
          getDocs(collection(db, 'Employees')),
          getDocs(collection(db, 'SalaryPayments'))
        ]);

        setEmployees(employeeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setPayments(paymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      } catch (error) {
        console.error('Failed to load pay salaries page:', error);
        swal('Error', 'Payroll could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const payrollRows = useMemo(() => {
    return summarizePayroll({
      employees,
      payments,
      periodKey
    }).sort((first, second) => {
      if (second.remainingAmount !== first.remainingAmount) {
        return second.remainingAmount - first.remainingAmount;
      }

      return second.expectedAmount - first.expectedAmount;
    });
  }, [employees, payments, periodKey]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return payrollRows.filter((employee) => {
      const employeeStatus = normalizeEmployeeStatus(employee.employeeStatus);
      const matchesStatus = statusFilter === 'All' || employeeStatus === statusFilter;

      if (!matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        employee.employeeName,
        employee.jobPosition,
        employee.campus,
        employee.employeeEmail,
        employee.employeePhone
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [payrollRows, searchTerm, statusFilter]);

  const payrollSummary = useMemo(() => {
    return payrollRows.reduce(
      (accumulator, employee) => {
        if (normalizeEmployeeStatus(employee.employeeStatus) === 'Archived') {
          return accumulator;
        }

        accumulator.expected += employee.expectedAmount;
        accumulator.paid += employee.paidAmount;
        accumulator.remaining += employee.remainingAmount;

        if (employee.remainingAmount <= 0 && employee.expectedAmount > 0) {
          accumulator.settled += 1;
        } else if (employee.paidAmount > 0) {
          accumulator.partial += 1;
        } else {
          accumulator.pending += 1;
        }

        return accumulator;
      },
      {
        expected: 0,
        paid: 0,
        remaining: 0,
        settled: 0,
        partial: 0,
        pending: 0
      }
    );
  }, [payrollRows]);

  const recentPayments = useMemo(() => {
    return [...payments]
      .sort((first, second) => {
        const firstTime = first.paymentDate?.seconds || first.createdAt?.seconds || 0;
        const secondTime = second.paymentDate?.seconds || second.createdAt?.seconds || 0;
        return secondTime - firstTime;
      })
      .slice(0, 8);
  }, [payments]);

  const employeesNeedingAction = payrollSummary.partial + payrollSummary.pending;
  const topOutstandingEmployee = filteredRows.find((employee) => employee.remainingAmount > 0)
    || payrollRows.find((employee) => employee.remainingAmount > 0)
    || filteredRows[0]
    || payrollRows[0]
    || null;

  const openTopOutstandingEmployee = () => {
    if (!topOutstandingEmployee) {
      swal('No employees', 'Add employees first, then open them to manage salary payments.', 'info');
      return;
    }

    navigate(`/view-employee/${topOutstandingEmployee.id}`);
  };

  return (
    <div className="clientsComp incomePage paySalariesPage">
      <section className="incomeHero paySalariesHero">
        <div className="incomeHeroCopy">
          <div className="incomeHeroTitleRow">
            <h5>Pay Salaries</h5>
          </div>
          <p className="paySalariesHeroText">
            Review this month&apos;s payroll position, see who still needs payment, and jump straight
            into the employee page to record the next salary entry.
          </p>
          <p className="incomeHeaderMeta">
            <span>{getMonthLabel(periodKey)}</span>
            <span className="incomeInlineDot" />
            <span>{filteredRows.length} employees in view</span>
            <span className="incomeInlineDot" />
            <span>{recentPayments.length} recent payments</span>
          </p>
        </div>
        <div className="incomeHeroActions paySalariesHeroActions">
          <button type="button" className="btn btn-dark" onClick={openTopOutstandingEmployee}>
            <FontAwesomeIcon icon={faMoneyCheckDollar} /> Open next employee
          </button>
        </div>
      </section>

      {loading ? (
        <div className="incomeStateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading payroll balances...</p>
        </div>
      ) : (
        <>
          <section className="incomeStatsGrid paySalariesStatsGrid">
            <article className="incomeStatCard incomeStatPrimary">
              <span className="incomeStatLabel">{getMonthLabel(periodKey)} payroll</span>
              <h3>{formatKes(payrollSummary.expected)}</h3>
              <p>Total salary expected across employees in the selected month.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Paid this period</span>
              <h3>{formatKes(payrollSummary.paid)}</h3>
              <p>Salary payments already recorded against this payroll month.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Outstanding</span>
              <h3>{formatKes(payrollSummary.remaining)}</h3>
              <p>Remaining payroll balance still open for this period.</p>
            </article>
            <article className="incomeStatCard">
              <span className="incomeStatLabel">Employees needing action</span>
              <h3>{employeesNeedingAction}</h3>
              <p>{payrollSummary.settled} settled, {payrollSummary.partial} partial, {payrollSummary.pending} unpaid.</p>
            </article>
          </section>

          <div className="searchContainer incomeToolbar paySalariesToolbar">
            <div className="paySalariesFilters">
              <Form.Control
                type="month"
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
                className="paySalariesControl"
              />
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search employee, role, campus..."
                className="paySalariesControl paySalariesSearch"
              />
              <Form.Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="paySalariesControl"
              >
                <option value="All">All statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Archived">Archived</option>
              </Form.Select>
            </div>
            <div className="incomeToolbarMeta">
              <span className="incomeToolbarText">Showing {filteredRows.length} of {payrollRows.length} employees</span>
            </div>
          </div>

          <section className="paySalariesLayout">
            <article className="incomeTableCard paySalariesMainCard">
              <div className="incomeTableHeader">
                <div>
                  <h6>Payroll Balances</h6>
                  <p>Open any employee to review salary history and record the next payment from their profile.</p>
                </div>
              </div>

              {filteredRows.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState paySalariesEmptyState">
                  <h6>No employees match this view</h6>
                  <p>Try another filter or add employees before managing salary payments.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle incomeRecordsTable paySalariesTable">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Salary</th>
                        <th>Paid</th>
                        <th>Remaining</th>
                        <th>Payroll status</th>
                        <th>Employee status</th>
                        <th>Open</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((employee) => (
                        <tr key={employee.id}>
                          <td>
                            <div className="paySalariesEmployeeCell">
                              <strong>{employee.employeeName || 'Employee'}</strong>
                              <span>{employee.jobPosition || 'Role not set'} • {employee.campus || 'Campus not set'}</span>
                            </div>
                          </td>
                          <td><span className="paySalariesAmountText">{formatKes(employee.expectedAmount)}</span></td>
                          <td><span className="paySalariesAmountText">{formatKes(employee.paidAmount)}</span></td>
                          <td><span className="paySalariesAmountText">{formatKes(employee.remainingAmount)}</span></td>
                          <td>
                            <span className={`opsStatusPill opsStatusPill--${employee.paymentTone}`}>{employee.paymentStatus}</span>
                          </td>
                          <td>
                            <span className={`employeeStatusBadge ${employeeStatusClass(employee.employeeStatus)}`}>
                              {normalizeEmployeeStatus(employee.employeeStatus)}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline-dark paySalariesActionBtn"
                              onClick={() => navigate(`/view-employee/${employee.id}`)}
                            >
                              <FontAwesomeIcon icon={faArrowRight} /> Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </article>

            <aside className="paySalariesSidebar">
              <div className="incomeTableCard paySalariesFocusCard">
                <div className="paySalariesSectionHeader">
                  <div className="paySalariesSectionIcon">
                    <FontAwesomeIcon icon={faMoneyCheckDollar} />
                  </div>
                  <div>
                    <h6>Next employee to handle</h6>
                    <p>The page prioritises the employee with the biggest remaining salary balance.</p>
                  </div>
                </div>

                {!topOutstandingEmployee ? (
                  <div className="incomeStateCard incomeEmptyState paySalariesCompactState">
                    <h6>No employee selected</h6>
                    <p>Employees will appear here once payroll records are available.</p>
                  </div>
                ) : (
                  <div className="paySalariesFocusBody">
                    <span className="paySalariesEyebrow">Outstanding now</span>
                    <strong>{formatKes(topOutstandingEmployee.remainingAmount)}</strong>
                    <p>{topOutstandingEmployee.employeeName || 'Employee'} • {topOutstandingEmployee.jobPosition || 'Role not set'}</p>
                    <div className="paySalariesFocusMeta">
                      <span>{topOutstandingEmployee.campus || 'Campus not set'}</span>
                      <span>{topOutstandingEmployee.paymentStatus}</span>
                    </div>
                    <button type="button" className="btn btn-dark" onClick={() => navigate(`/view-employee/${topOutstandingEmployee.id}`)}>
                      Open employee page
                    </button>
                  </div>
                )}
              </div>

              <div className="incomeTableCard paySalariesSideCard">
                <div className="paySalariesSectionHeader">
                  <div className="paySalariesSectionIcon">
                    <FontAwesomeIcon icon={faCoins} />
                  </div>
                  <div>
                    <h6>Recent salary payments</h6>
                    <p>Latest payroll activity across the company.</p>
                  </div>
                </div>

                {recentPayments.length === 0 ? (
                  <div className="incomeStateCard incomeEmptyState paySalariesCompactState">
                    <h6>No salary payments recorded</h6>
                    <p>The most recent payroll entries will appear here after the first salary payment is saved.</p>
                  </div>
                ) : (
                  <div className="paySalariesRecentList">
                    {recentPayments.map((payment) => (
                      <div key={payment.id} className="paySalariesRecentRow">
                        <div>
                          <strong>{payment.employeeName || 'Employee'}</strong>
                          <span>{payment.salaryPeriodLabel || 'Current period'} • {payment.method || 'Method not set'}</span>
                        </div>
                        <div className="paySalariesRecentAside">
                          <strong>{formatKes(payment.amount)}</strong>
                          <span>{formatDateTime(payment.paymentDate)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="incomeTableCard paySalariesSideCard">
                <div className="paySalariesSectionHeader">
                  <div className="paySalariesSectionIcon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <h6>How to use this page</h6>
                    <p>Keep payroll review lightweight here, then complete the payment inside the employee profile.</p>
                  </div>
                </div>

                <div className="paySalariesChecklist">
                  <div className="paySalariesChecklistItem">
                    <strong>1. Pick the month</strong>
                    <span>Switch the payroll month at the top before reviewing balances.</span>
                  </div>
                  <div className="paySalariesChecklistItem">
                    <strong>2. Open the employee</strong>
                    <span>Use the table or the focus card to jump to the right employee page.</span>
                  </div>
                  <div className="paySalariesChecklistItem">
                    <strong>3. Record the payment</strong>
                    <span>The actual salary payment entry is saved from the employee profile.</span>
                  </div>
                </div>
              </div>
            </aside>
          </section>
        </>
      )}
    </div>
  );
}

export default PaySalariesComp;
